import React, { useState, useCallback, useRef, useEffect, useImperativeHandle, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    Divider,
    FormGroup,
    Grid,
    Radio,
    RadioGroup,
    TextField,
    Button,
    Typography,
    Stepper,
    Step,
    StepLabel,
    MenuItem,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    Alert,
    InputAdornment,
} from "@mui/material";
import { checkPostalCode } from "../../../services/postalService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import QuizIcon from "@mui/icons-material/Quiz";
import { toast } from "react-toastify";
import { useLanguage } from "../../../context/LanguageContext";
import { CUSTOMER_ACTIONS, useRights } from "../../../rights";
import { PageContainer, PageTitle, StoreAutocomplete } from "../../../components/Common";
import { useAddPerson } from "../../../hooks/Clients/new";
import { useStoreList } from "../../../context/StoreListContext";
import { useStoreData } from "../../../context/StoreDataContext";
import { useAuthenticatedFetchWithWorkspace } from "../../../hooks/Keycloak/useAuthenticatedFetchWithWorkspace";
import { NewCustomerPayload } from "../../../types/Customer";
import { Store } from "../../../types/Store";
import type { Country, WayType } from "../../../context/StoreDataContext";
import { parseActiveFormRows, type ParsedForm, type ParsedFormSection, type ActiveFormRow } from "../../../hooks/Clients/dashboard/useGetActiveForms";

const INITIAL_FORM: NewCustomerPayload = {
    civility: "",
    lastname: "",
    firstname: "",
    birth_date: "",
    identifier: "",
    store_id: "",
    address1: "",
    address2: "",
    address3_num: "",
    address3_type: "",
    address3_name: "",
    address4: "",
    zip_code: "",
    city: "",
    country: "",
    email: "",
    cellphone: "",
    phone: "",
    dialing_code: "+33",
    solicitation_mail: "0",
    solicitation_email: "0",
    solicitation_sms: "0",
    solicitation_trusted_mail: "0",
    solicitation_trusted_email: "0",
    solicitation_trusted_sms: "0",
};

const CIVILITIES = ["M.", "Mme"];

interface StepComponentProps {
    form: NewCustomerPayload;
    onChange?: (field: keyof NewCustomerPayload, value: string) => void;
    t: (key: string) => string;
    stores?: Store[] | null;
    storesLoading?: boolean;
    cardValidating?: boolean;
    cardError?: string | null;
    onCardBlur?: () => void;
    wayTypes?: WayType[];
    countries?: Country[];
    storeDataLoading?: boolean;
}

type StepHandle = { getValues: () => Partial<NewCustomerPayload> };

const FORM_FIELD_PATTERN = /^(boolean|numeric|text|date)(0[1-9]|1[0-9]|20)$/;
const NUMERIC_PATTERN = /^-?\d+$/;

const getCheckboxValues = (value: string, optionValues: string[]): string[] => {
    if (!value) return [];
    if (value.includes(',')) return value.split(',').map(v => v.trim()).filter(Boolean);

    return optionValues.filter(optionValue => {
        const normalizedOptionValue = String(optionValue);
        if (!normalizedOptionValue) return false;
        if (normalizedOptionValue === "0") return value === "0";
        return value.includes(normalizedOptionValue);
    });
};

const serializeCheckboxValues = (values: string[]): string => {
    if (values.includes("0")) return "0";
    return values.join("");
};

const buildCreatedCnilData = (form: NewCustomerPayload): Record<string, boolean> => ({
    mailCompany: form.solicitation_mail === "1",
    emailCompany: form.solicitation_email === "1",
    smsCompany: form.solicitation_sms === "1",
    mailTrusted: form.solicitation_trusted_mail === "1",
    emailTrusted: form.solicitation_trusted_email === "1",
    smsTrusted: form.solicitation_trusted_sms === "1",
});

const normalizeFormAnswers = (answers: Record<string, string>): Record<string, string> => {
    return Object.fromEntries(
        Object.entries(answers).flatMap(([rawKey, rawValue]) => {
            const key = rawKey.trim().toLowerCase();
            const value = rawValue.trim();

            if (!key || !value) return [];
            if (key === "answeredformid") return [["answeredFormId", value]];
            if (!FORM_FIELD_PATTERN.test(key)) return [];
            if ((key.startsWith("numeric") || key.startsWith("boolean")) && !NUMERIC_PATTERN.test(value)) return [];

            return [[key, value]];
        })
    );
};

const getCreatedPersonId = (result: Record<string, unknown>): string | number | null => {
    const candidates = [
        result.person_id,
        result.personId,
        result.PERSONID,
        result.PERSON_ID,
    ];
    const nestedCandidates = [
        result.customer,
        result.person,
        result.data,
    ]
        .filter((value): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value))
        .flatMap((value) => [value.person_id, value.personId, value.PERSONID, value.PERSON_ID]);

    const personId = [...candidates, ...nestedCandidates].find((value) => value != null && String(value).trim() !== "");
    return typeof personId === "string" || typeof personId === "number" ? personId : null;
};

const PersonalInfoStep = React.forwardRef<StepHandle, StepComponentProps>(
    ({ form, onChange, t, stores, storesLoading, cardValidating, cardError, onCardBlur }, ref) => {
        const [civility, setCivility] = useState(form.civility || '');
        const [identifier, setIdentifier] = useState(form.identifier || '');
        const [storeId, setStoreId] = useState(form.store_id || '');
        const lastnameRef = useRef<HTMLInputElement>(null);
        const firstnameRef = useRef<HTMLInputElement>(null);
        const birthdateRef = useRef<HTMLInputElement>(null);

        useImperativeHandle(ref, () => ({
            getValues: () => ({
                civility,
                lastname: lastnameRef.current?.value || '',
                firstname: firstnameRef.current?.value || '',
                birth_date: birthdateRef.current?.value || '',
                identifier,
                store_id: storeId,
            })
        }));

        const handleIdentifierChange = useCallback((value: string) => {
            const v = value.replace(/\D/g, '').slice(0, 13);
            setIdentifier(v);
            onChange?.("identifier", v);
        }, [onChange]);

        return (
    <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 2 }}>
            <TextField
                fullWidth
                select
                size="small"
                label={t("pages.customers.newPerson.form.civility")}
                value={civility}
                onChange={(e) => setCivility(e.target.value)}
                helperText=" "
            >
                <MenuItem value="">—</MenuItem>
                {CIVILITIES.map((c) => (
                    <MenuItem key={c} value={c}>
                        {c}
                    </MenuItem>
                ))}
            </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
                fullWidth
                required
                size="small"
                label={t("pages.customers.newPerson.form.lastname")}
                defaultValue={form.lastname}
                inputRef={lastnameRef}
                helperText=" "
            />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
                fullWidth
                size="small"
                label={t("pages.customers.newPerson.form.firstname")}
                defaultValue={form.firstname}
                inputRef={firstnameRef}
                helperText=" "
            />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
                fullWidth
                size="small"
                label={t("pages.customers.newPerson.form.birthdate")}
                type="date"
                defaultValue={form.birth_date}
                inputRef={birthdateRef}
                InputLabelProps={{ shrink: true }}
                inputProps={{ onClick: (e: React.MouseEvent<HTMLInputElement>) => { (e.target as HTMLInputElement).showPicker?.(); } }}
                helperText=" "
            />
        </Grid>

        <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
                fullWidth
                size="small"
                label={t("pages.customers.newPerson.form.cardNumber")}
                value={identifier}
                onChange={(e) => handleIdentifierChange(e.target.value)}
                onBlur={onCardBlur}
                error={!!cardError}
                helperText={cardError || ""}
                inputProps={{ maxLength: 13 }}
                InputProps={{
                    endAdornment: cardValidating ? (
                        <InputAdornment position="end">
                            <CircularProgress size={16} />
                        </InputAdornment>
                    ) : undefined,
                }}
            />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
            <StoreAutocomplete
                stores={stores}
                loading={storesLoading}
                value={storeId}
                onChange={(id) => {
                    setStoreId(id);
                    onChange?.("store_id", id);
                }}
                label={t("pages.customers.newPerson.form.store")}
                required
                helperText=" "
                showStoreTypeIcon={false}
            />
        </Grid>
    </Grid>
        );
    }
);

const ContactStep = React.forwardRef<StepHandle, StepComponentProps>(
    ({ form, t, wayTypes = [], countries = [], storeDataLoading = false }, ref) => {
        const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
        const [postalLoading, setPostalLoading] = useState(false);
        const [zip, setZip] = useState(form.zip_code || '');
        const [city, setCity] = useState(form.city || '');
        const [address3Type, setAddress3Type] = useState(form.address3_type || '');
        const [country, setCountry] = useState(form.country || '');
        const zipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

        const address1Ref = useRef<HTMLInputElement>(null);
        const address2Ref = useRef<HTMLInputElement>(null);
        const address3NumRef = useRef<HTMLInputElement>(null);
        const address3NameRef = useRef<HTMLInputElement>(null);
        const emailRef = useRef<HTMLInputElement>(null);
        const dialingCodeRef = useRef<HTMLInputElement>(null);
        const cellphoneRef = useRef<HTMLInputElement>(null);
        const phoneRef = useRef<HTMLInputElement>(null);

        useImperativeHandle(ref, () => ({
            getValues: () => ({
                address1: address1Ref.current?.value || '',
                address2: address2Ref.current?.value || '',
                address3_num: address3NumRef.current?.value || '',
                address3_type: address3Type,
                address3_name: address3NameRef.current?.value || '',
                zip_code: zip,
                city,
                country,
                email: emailRef.current?.value || '',
                dialing_code: dialingCodeRef.current?.value || '+33',
                cellphone: cellphoneRef.current?.value || '',
                phone: phoneRef.current?.value || '',
            })
        }));

        const handleZipChange = (value: string) => {
            const digits = value.replace(/\D/g, '').slice(0, 5);
            setZip(digits);
            if (!/^\d{5}$/.test(digits)) { setCitySuggestions([]); return; }
            if (zipTimer.current) clearTimeout(zipTimer.current);
            zipTimer.current = setTimeout(async () => {
                setPostalLoading(true);
                const cities = await checkPostalCode(digits);
                setCitySuggestions(cities);
                if (cities.length === 1) setCity(cities[0]);
                setPostalLoading(false);
            }, 400);
        };

        return (
    <Grid container spacing={2}>
        <Grid size={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t("pages.customers.dashboard.contact.address")}
            </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
                fullWidth size="small"
                label={t("pages.customers.newPerson.form.address1")}
                defaultValue={form.address1}
                inputRef={address1Ref}
            />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
                fullWidth size="small"
                label={t("pages.customers.newPerson.form.address2")}
                defaultValue={form.address2}
                inputRef={address2Ref}
            />
        </Grid>
        <Grid size={{ xs: 12, sm: 2 }}>
            <TextField
                fullWidth size="small"
                label={t("pages.customers.newPerson.form.number")}
                defaultValue={form.address3_num}
                inputRef={address3NumRef}
            />
        </Grid>
        <Grid size={{ xs: 12, sm: 2 }}>
            <TextField
                fullWidth
                select
                size="small"
                label={t("pages.customers.searchForm.streetType")}
                value={address3Type}
                onChange={(e) => setAddress3Type(e.target.value)}
                disabled={storeDataLoading}
                SelectProps={{ MenuProps: { sx: { height: "300px" } } }}
            >
                <MenuItem value="">—</MenuItem>
                {storeDataLoading && <MenuItem disabled>{t("common.loading")}...</MenuItem>}
                {!storeDataLoading && wayTypes.length === 0 && <MenuItem disabled>{t("common.noData")}</MenuItem>}
                {!storeDataLoading && wayTypes.map((type) => (
                    <MenuItem key={String(type.id)} value={String(type.id)}>
                        {type.name}
                    </MenuItem>
                ))}
            </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
                fullWidth size="small"
                label={t("pages.customers.newPerson.form.wayName")}
                defaultValue={form.address3_name}
                inputRef={address3NameRef}
            />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
                fullWidth size="small"
                label={t("pages.customers.newPerson.form.zipCode")}
                value={zip}
                inputProps={{ maxLength: 5 }}
                InputProps={{ endAdornment: postalLoading ? <CircularProgress size={14} /> : undefined }}
                onChange={(e) => handleZipChange(e.target.value)}
            />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
            <Autocomplete
                freeSolo
                options={citySuggestions}
                value={city}
                onInputChange={(_, value) => setCity(value)}
                renderInput={(params) => (
                    <TextField {...params} fullWidth size="small"
                        label={t("pages.customers.newPerson.form.city")}
                        inputProps={{ ...params.inputProps, maxLength: 38 }}
                    />
                )}
            />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
                fullWidth
                select
                size="small"
                label={t("pages.customers.newPerson.form.country")}
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                disabled={storeDataLoading}
                SelectProps={{ MenuProps: { sx: { height: "300px" } } }}
            >
                <MenuItem value="">—</MenuItem>
                {storeDataLoading && <MenuItem disabled>{t("common.loading")}...</MenuItem>}
                {!storeDataLoading && countries.length === 0 && <MenuItem disabled>{t("common.noData")}</MenuItem>}
                {!storeDataLoading && countries.map((item) => (
                    <MenuItem key={String(item.id)} value={item.name}>
                        {item.name}
                    </MenuItem>
                ))}
            </TextField>
        </Grid>
        <Grid size={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                {t("pages.customers.dashboard.contact.contact")}
            </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
                fullWidth size="small"
                label={t("pages.customers.newPerson.form.email")}
                type="email"
                defaultValue={form.email}
                inputRef={emailRef}
            />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
                fullWidth size="small"
                label={t("pages.customers.newPerson.form.dialingCode")}
                defaultValue={form.dialing_code}
                inputRef={dialingCodeRef}
            />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
                fullWidth size="small"
                label={t("pages.customers.newPerson.form.cellphone")}
                defaultValue={form.cellphone}
                inputRef={cellphoneRef}
            />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
                fullWidth size="small"
                label={t("pages.customers.newPerson.form.phone")}
                defaultValue={form.phone}
                inputRef={phoneRef}
            />
        </Grid>
    </Grid>
        );
    }
);

const CNILStep = React.forwardRef<StepHandle, StepComponentProps>(
    ({ form, t }, ref) => {
        const [solicitationMail, setSolicitationMail] = useState(form.solicitation_mail === '1');
        const [solicitationEmail, setSolicitationEmail] = useState(form.solicitation_email === '1');
        const [solicitationSms, setSolicitationSms] = useState(form.solicitation_sms === '1');
        const [trustedMail, setTrustedMail] = useState(form.solicitation_trusted_mail === '1');
        const [trustedEmail, setTrustedEmail] = useState(form.solicitation_trusted_email === '1');
        const [trustedSms, setTrustedSms] = useState(form.solicitation_trusted_sms === '1');

        useImperativeHandle(ref, () => ({
            getValues: () => ({
                solicitation_mail: solicitationMail ? '1' : '0',
                solicitation_email: solicitationEmail ? '1' : '0',
                solicitation_sms: solicitationSms ? '1' : '0',
                solicitation_trusted_mail: trustedMail ? '1' : '0',
                solicitation_trusted_email: trustedEmail ? '1' : '0',
                solicitation_trusted_sms: trustedSms ? '1' : '0',
            })
        }));

        return (
    <Grid container spacing={1}>
        <Grid size={12}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("pages.customers.newPerson.cnilDescription")}
            </Typography>
        </Grid>
        <Grid size={12}>
            <Typography variant="subtitle2" fontWeight={600}>
                {t("pages.customers.newPerson.cnil.companySolicitations")}
            </Typography>
        </Grid>
        <Grid size={12}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={solicitationMail}
                        onChange={(e) => setSolicitationMail(e.target.checked)}
                    />
                }
                label={t("pages.customers.newPerson.form.solicitationMail")}
            />
        </Grid>
        <Grid size={12}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={solicitationEmail}
                        onChange={(e) => setSolicitationEmail(e.target.checked)}
                    />
                }
                label={t("pages.customers.newPerson.form.solicitationEmail")}
            />
        </Grid>
        <Grid size={12}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={solicitationSms}
                        onChange={(e) => setSolicitationSms(e.target.checked)}
                    />
                }
                label={t("pages.customers.newPerson.form.solicitationSms")}
            />
        </Grid>
        <Grid size={12}><Divider sx={{ my: 1 }} /></Grid>
        <Grid size={12}>
            <Typography variant="subtitle2" fontWeight={600}>
                {t("pages.customers.newPerson.cnil.trustedSolicitations")}
            </Typography>
        </Grid>
        <Grid size={12}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={trustedMail}
                        onChange={(e) => setTrustedMail(e.target.checked)}
                    />
                }
                label={t("pages.customers.newPerson.form.trustedSolicitationMail")}
            />
        </Grid>
        <Grid size={12}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={trustedEmail}
                        onChange={(e) => setTrustedEmail(e.target.checked)}
                    />
                }
                label={t("pages.customers.newPerson.form.trustedSolicitationEmail")}
            />
        </Grid>
        <Grid size={12}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={trustedSms}
                        onChange={(e) => setTrustedSms(e.target.checked)}
                    />
                }
                label={t("pages.customers.newPerson.form.trustedSolicitationSms")}
            />
        </Grid>
    </Grid>
        );
    }
);

interface FormStepHandle {
    getFormData: () => { formId: string; version: string; answers: Record<string, string> } | null;
}

const FormStep = React.forwardRef<FormStepHandle, { t: (key: string) => string }>(
    ({ t }, ref) => {
        const { authFetch, isReady, workspaceId } = useAuthenticatedFetchWithWorkspace();
        const [forms, setForms] = useState<ParsedForm[]>([]);
        const [loadingForms, setLoadingForms] = useState(false);
        const [answers, setAnswers] = useState<Record<string, string>>({});

        useEffect(() => {
            if (!isReady || !workspaceId) return;
            setLoadingForms(true);
            const params = new URLSearchParams({ workspace: workspaceId });
            authFetch(`/activeForms?${params.toString()}`, { method: 'GET' })
                .then((data) => {
                    if (Array.isArray(data)) {
                        const parsed: ParsedForm[] = (data as ActiveFormRow[]).map((form) => {
                            const raw = form as Record<string, unknown>;
                            const frames = raw.FRAMES;
                            const formId = String(raw.FORM_ID ?? '');
                            const rows: ActiveFormRow[] = [];
                            if (frames) {
                                const frameList: ActiveFormRow[] = Array.isArray(frames) ? (frames as ActiveFormRow[]) : [frames as ActiveFormRow];
                                const extractRows = (nodes: ActiveFormRow[]): ActiveFormRow[] => {
                                    const r: ActiveFormRow[] = [];
                                    for (const node of nodes) {
                                        const qid = node.FORM_QUESTION_ID;
                                        if (qid != null && String(qid) !== '' && String(qid) !== '-1') r.push({ ...node, SELECTED: false });
                                        const children = (node as Record<string, unknown>).children as ActiveFormRow[] | undefined;
                                        if (Array.isArray(children)) r.push(...extractRows(children));
                                    }
                                    return r;
                                };
                                rows.push(...extractRows(frameList));
                            }
                            return { formId, name: String(raw.NAME ?? ''), version: String(raw.VERSION ?? ''), rows };
                        });
                        setForms(parsed);
                    }
                })
                .catch(() => {})
                .finally(() => setLoadingForms(false));
        }, [isReady, workspaceId, authFetch]);

        const latestForm = useMemo(() => {
            if (!forms.length) return undefined;
            return [...forms].sort((a, b) => (parseFloat(b.version) || 0) - (parseFloat(a.version) || 0))[0];
        }, [forms]);

        const sections = useMemo<ParsedFormSection[]>(() => latestForm ? parseActiveFormRows(latestForm.rows) : [], [latestForm]);

        const handleRadioChange = useCallback((dbFieldName: string, value: string) => {
            setAnswers(prev => ({ ...prev, [dbFieldName]: value }));
        }, []);

        const handleCheckboxChange = useCallback((dbFieldName: string, value: string, checked: boolean, optionValues: string[]) => {
            setAnswers(prev => {
                const normalizedValue = String(value);
                const normalizedOptionValues = optionValues.map(String);
                const current = getCheckboxValues(prev[dbFieldName] ?? "", normalizedOptionValues);
                const next = checked
                    ? normalizedValue === "0"
                        ? ["0"]
                        : [...new Set([...current.filter(v => v !== "0"), normalizedValue])]
                    : current.filter(v => v !== normalizedValue);

                const normalizedNext = next.length === 0 && normalizedOptionValues.includes("0") ? ["0"] : next;
                return { ...prev, [dbFieldName]: serializeCheckboxValues(normalizedNext) };
            });
        }, []);

        const handleTextChange = useCallback((dbFieldName: string, value: string) => {
            setAnswers(prev => ({ ...prev, [dbFieldName]: value }));
        }, []);

        useImperativeHandle(ref, () => ({
            getFormData: () => {
                if (!latestForm) return null;
                return { formId: latestForm.formId, version: latestForm.version, answers };
            }
        }));

        if (loadingForms) {
            return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
        }

        if (!latestForm) {
            return (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <QuizIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">Aucun questionnaire disponible</Typography>
                </Box>
            );
        }

        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <QuizIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle1" fontWeight={700} color="primary.dark">
                        {latestForm.name || 'Questionnaire Client'}
                        {latestForm.version && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                v{latestForm.version}
                            </Typography>
                        )}
                    </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', alignItems: 'start' }}>
                    {sections.map((section) => (
                        <Box key={section.parentId} sx={{ minWidth: 0, p: 1.5, borderRight: '1px solid', borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderRight: 0 } }}>
                            {section.questions.map((q) => {
                                const sortedOpts = [...q.options].sort((a, b) => a.sortOrder - b.sortOrder);
                                const optionValues = sortedOpts.map(opt => String(opt.value));
                                const currentValue = answers[q.dbFieldName] ?? '';
                                const currentValues = getCheckboxValues(currentValue, optionValues);
                                return (
                                    <Box key={q.questionId} sx={{ mb: 2.5 }}>
                                        <Typography
                                            variant="body2" fontWeight={700} color="text.primary"
                                            sx={{ mb: 0.75, fontSize: '0.82rem', borderLeft: '3px solid', borderColor: 'primary.light', pl: 1, lineHeight: 1.4 }}
                                        >
                                            {q.question}
                                        </Typography>
                                        {q.answerType === 'radio' && (
                                            <RadioGroup
                                                value={currentValue}
                                                onChange={(e) => handleRadioChange(q.dbFieldName, e.target.value)}
                                            >
                                                {sortedOpts.map((opt, i) => (
                                                    <FormControlLabel
                                                        key={i} value={opt.value}
                                                        control={<Radio size="small" sx={{ py: 0.2 }} />}
                                                        label={<Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{opt.answer}</Typography>}
                                                        sx={{ ml: 0, mr: 0 }}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        )}
                                        {q.answerType === 'checkbox' && (
                                            <FormGroup>
                                                {sortedOpts.map((opt, i) => (
                                                    <FormControlLabel
                                                        key={i}
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={currentValues.includes(String(opt.value))}
                                                                onChange={(e) => handleCheckboxChange(q.dbFieldName, String(opt.value), e.target.checked, optionValues)}
                                                                sx={{ py: 0.2 }}
                                                            />
                                                        }
                                                        label={<Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{opt.answer}</Typography>}
                                                        sx={{ ml: 0, mr: 0 }}
                                                    />
                                                ))}
                                            </FormGroup>
                                        )}
                                        {(q.answerType === 'date_month_year' || q.answerType === 'date') && (
                                            <TextField
                                                fullWidth size="small" type="date"
                                                value={currentValue}
                                                onChange={(e) => handleTextChange(q.dbFieldName, e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                        {q.answerType !== 'radio' && q.answerType !== 'checkbox' && q.answerType !== 'date_month_year' && q.answerType !== 'date' && (
                                            <TextField
                                                fullWidth size="small"
                                                value={currentValue}
                                                onChange={(e) => handleTextChange(q.dbFieldName, e.target.value)}
                                                placeholder={q.question}
                                            />
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }
);

const NewClients = (): React.ReactElement => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const { addPerson, isLoading, error } = useAddPerson();
    const { stores: allStores, loading: storesLoading } = useStoreList();
    const { wayTypes, countries, loading: storeDataLoading } = useStoreData();
    const stores = useMemo(
        () => allStores?.filter((s) => s.ENTITY_TYPE === 'STORE') ?? null,
        [allStores]
    );
    const { authFetch, isReady, workspaceId } = useAuthenticatedFetchWithWorkspace();
    const submittingRef = useRef(false);
    const cnilValuesRef = useRef<Partial<NewCustomerPayload>>({});
    const step0Ref = useRef<StepHandle>(null);
    const step1Ref = useRef<StepHandle>(null);
    const step2Ref = useRef<StepHandle>(null);
    const step3Ref = useRef<FormStepHandle>(null);

    const [activeStep, setActiveStep] = useState(0);
    const [form, setForm] = useState<NewCustomerPayload>(INITIAL_FORM);
    const [cardValidating, setCardValidating] = useState(false);
    const [cardError, setCardError] = useState<string | null>(null);
    const [cardValid, setCardValid] = useState<boolean | null>(null);

    // Étape questionnaire seulement avec da.customer.change_form (BeOne : creation/actions:224)
    const { can } = useRights();
    const canFillQuestionnaire = can(CUSTOMER_ACTIONS.questionnaire);
    const steps = [
        t("pages.customers.newPerson.steps.personalInfo"),
        t("pages.customers.newPerson.steps.contact"),
        t("pages.customers.newPerson.steps.cnil"),
        ...(canFillQuestionnaire ? [t("pages.customers.newPerson.steps.form")] : []),
    ];

    const handleChange = useCallback(
        (field: keyof NewCustomerPayload, value: string) => {
            setForm((prev) => ({ ...prev, [field]: value }));
            if (field === "identifier") {
                setCardError(null);
                setCardValid(null);
            }
        },
        []
    );

    const validateCard = useCallback(async () => {
        const identifier = (form.identifier ?? "").trim();
        if (!identifier || !isReady || !workspaceId) return;
        setCardValidating(true);
        setCardError(null);
        try {
            const params = new URLSearchParams({ workspace: workspaceId, identifier });
            const result = await authFetch(`/isValidAndNotUsedCard?${params.toString()}`, { method: "GET" }) as Record<string, unknown>;
            const isValid = result?.valid === true || result?.isValid === true || result?.status === "valid" || result?.result === true;
            if (!isValid) {
                setCardError("Ce numéro de carte est invalide ou déjà utilisé.");
                setCardValid(false);
            } else {
                setCardValid(true);
            }
        } catch {
            setCardError("Impossible de vérifier la carte. Veuillez réessayer.");
            setCardValid(false);
        } finally {
            setCardValidating(false);
        }
    }, [form.identifier, authFetch, isReady, workspaceId]);

    useEffect(() => {
        const identifier = (form.identifier ?? "").trim();
        if (identifier.length === 13 && /^\d{13}$/.test(identifier)) {
            validateCard();
        }
    }, [form.identifier, validateCard]);

    const isStepValid = useCallback(() => {
        if (activeStep === 0) {
            if ((form.identifier ?? "").trim() && cardValid === false) return false;
            if ((form.identifier ?? "").trim() && cardValidating) return false;
            return true;
        }
        return true;
    }, [activeStep, form.identifier, cardValid, cardValidating]);

    const handleNext = useCallback(() => {
        if (activeStep === 0) {
            const vals = step0Ref.current?.getValues() ?? {};
            if (!vals.lastname?.trim()) {
                toast.warning(t("pages.customers.newPerson.lastnameRequired"));
                return;
            }
            if (!vals.store_id) {
                toast.warning(t("pages.customers.newPerson.storeRequired"));
                return;
            }
            setForm(prev => ({ ...prev, ...vals }));
        } else if (activeStep === 1) {
            const vals = step1Ref.current?.getValues() ?? {};
            setForm(prev => ({ ...prev, ...vals }));
        } else if (activeStep === 2) {
            const vals = step2Ref.current?.getValues() ?? {};
            cnilValuesRef.current = vals;
            setForm(prev => ({ ...prev, ...vals }));
        }
        setActiveStep((s) => s + 1);
    }, [activeStep, t]);

    const handleBack = useCallback(() => {
        setActiveStep((s) => s - 1);
    }, []);

    const handleSubmit = useCallback(async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        try {
            const step0Values = step0Ref.current?.getValues() ?? {};
            const step1Values = step1Ref.current?.getValues() ?? {};
            const step2Values = step2Ref.current?.getValues() ?? cnilValuesRef.current;
            const finalForm: NewCustomerPayload = {
                ...form,
                ...step0Values,
                ...step1Values,
                ...cnilValuesRef.current,
                ...step2Values,
            };

            if (!finalForm.lastname.trim()) {
                toast.warning(t("pages.customers.newPerson.lastnameRequired"));
                return;
            }
            if (!finalForm.store_id) {
                toast.warning(t("pages.customers.newPerson.storeRequired"));
                return;
            }

            const formData = step3Ref.current?.getFormData();
            const formsData: Record<string, Record<string, string>> = {};
            if (formData?.formId && Object.keys(formData.answers).length > 0) {
                const normalizedAnswers = normalizeFormAnswers(formData.answers);
                if (Object.keys(normalizedAnswers).length > 0) {
                    formsData[`form_${formData.formId}`] = normalizedAnswers;
                }
            }

            const result = await addPerson(finalForm, formsData);
            if (!result) {
                // error state is already set by useAddPerson; also show a toast for visibility
                toast.error(t("pages.customers.newPerson.error"));
                return;
            }
            const rawResult = result as unknown as Record<string, unknown>;
            const personId = getCreatedPersonId(rawResult);

            toast.success(t("pages.customers.newPerson.success"));
            if (personId) {
                navigate(`/clients/dashboard/${personId}`, { state: {
                        personId,
                        createdFormsData: formsData,
                        createdCnilData: buildCreatedCnilData(finalForm),
                    },
                });
            } else {
                navigate("/clients/search");
            }
        } finally {
            submittingRef.current = false;
        }
    }, [addPerson, form, navigate, t]);

    return (
        <PageContainer>
            <PageTitle>{t("pages.customers.newPerson.title")}</PageTitle>

            <Card>
                <CardContent>
                    <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {t("pages.customers.newPerson.error")}: {error}
                        </Alert>
                    )}

                    <Box sx={{ minHeight: 200 }}>
                        {activeStep === 0 && <PersonalInfoStep ref={step0Ref} form={form} onChange={handleChange} t={t} stores={stores} storesLoading={storesLoading} cardValidating={cardValidating} cardError={cardError} onCardBlur={validateCard} />}
                        {activeStep === 1 && <ContactStep ref={step1Ref} form={form} t={t} wayTypes={wayTypes} countries={countries} storeDataLoading={storeDataLoading} />}
                        {activeStep === 2 && <CNILStep ref={step2Ref} form={form} t={t} />}
                        {activeStep === 3 && canFillQuestionnaire && <FormStep ref={step3Ref} t={t} />}
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
                        {activeStep > 0 ? (
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBack}
                                disabled={isLoading}
                            >
                                {t("pages.customers.newPerson.back")}
                            </Button>
                        ) : (
                            <Box />
                        )}

                        {activeStep < steps.length - 1 ? (
                            <Button
                                variant="contained"
                                endIcon={<ArrowForwardIcon />}
                                onClick={handleNext}
                                disabled={!isStepValid()}
                            >
                                {t("pages.customers.newPerson.next")}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                startIcon={
                                    isLoading ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />
                                }
                                onClick={handleSubmit}
                                disabled={isLoading}
                            >
                                {isLoading
                                    ? t("pages.customers.newPerson.submitting")
                                    : t("pages.customers.newPerson.submit")}
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </PageContainer>
    );
};

export default NewClients;
